import { type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { useDismissable } from '@/hooks/use-dismissable'

export interface DropdownOption<T> {
  value: T
  label: string
  disabled?: boolean
}

interface DropdownProps<T> {
  /** Icon rendered in the field's prepend slot. */
  icon?: ReactNode
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  placeholder?: string
  label: string
  className?: string
}

/**
 * The `.field-box.dropdown` control, as a reusable component.
 *
 * The original repeated this markup seven times and drove it with
 * `document.getElementById('...-dropdown').classList.toggle('show')`, so each
 * instance needed a unique hard-coded id and a matching `toggleXDropdown()`
 * method. It also listed only the *unselected* options, which made the current
 * value impossible to re-pick after the list re-rendered; the full list is
 * shown here with the active option marked.
 */
export function Dropdown<T extends string | number>({
  icon,
  value,
  options,
  onChange,
  placeholder = '',
  label,
  className,
}: DropdownProps<T>) {
  const { ref, isOpen, toggle, close } = useDismissable<HTMLDivElement>()
  const selected = options.find((option) => option.value === value)

  return (
    <div
      className={cn('field-box dropdown', isOpen && 'open', className)}
      ref={ref}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label={label}
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          toggle()
        }
      }}
    >
      {icon ? <div className="field-box-prepend">{icon}</div> : null}
      <div className="field-box-content">{selected?.label ?? placeholder}</div>
      <div className="field-box-append">
        <img src="/icons/drop.png" width={13} height={8} alt="" />
      </div>
      <div className={cn('field-dropdown-content', isOpen && 'show')} role="listbox">
        {options.map((option) => (
          <div
            key={String(option.value)}
            role="option"
            aria-selected={option.value === value}
            aria-disabled={option.disabled}
            tabIndex={-1}
            className={cn(
              'field-dropdown-row',
              option.disabled && 'disabled',
              option.value === value && 'highlight',
            )}
            onClick={(event) => {
              event.stopPropagation()
              if (option.disabled) return
              onChange(option.value)
              close()
            }}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  )
}
